import styled from '@emotion/styled'
import { Popover, Typography } from '@mui/material'
import { PlusCircle } from '@emotion-icons/fa-solid/PlusCircle'
import { useState } from 'react'
import { TopToolbarIconBox, TopToolbarIconBoxContent } from 'src/components/insights/top-toolbar'
import { Colors, Shapes } from 'src/components/theme'
import { FANCY_SCROLLBAR } from 'src/utils/styles'
import { HandPeace } from '@emotion-icons/fa-solid/HandPeace'
import { SmileWink } from '@emotion-icons/fa-solid/SmileWink'
import { Building } from '@emotion-icons/fa-solid/Building'
import { ChartArea } from '@emotion-icons/fa-solid/ChartArea'
import { ChartBar } from '@emotion-icons/fa-solid/ChartBar'
import { ChartLine } from '@emotion-icons/fa-solid/ChartLine'
import { ChartPie } from '@emotion-icons/fa-solid/ChartPie'
import { Star } from '@emotion-icons/fa-solid/Star'
import { Certificate } from '@emotion-icons/fa-solid/Certificate'
import { Briefcase } from '@emotion-icons/fa-solid/Briefcase'
import { Bolt } from '@emotion-icons/fa-solid/Bolt'
import { DrawPolygon } from '@emotion-icons/fa-solid/DrawPolygon'
import { FileAlt } from '@emotion-icons/fa-solid/FileAlt'
import { FolderOpen } from '@emotion-icons/fa-solid/FolderOpen'
import { Archive } from '@emotion-icons/fa-solid/Archive'
import { Key } from '@emotion-icons/fa-solid/Key'
import { Road } from '@emotion-icons/fa-solid/Road'
import { DollarSign } from '@emotion-icons/fa-solid/DollarSign'
import { Paperclip } from '@emotion-icons/fa-solid/Paperclip'
import { Tags } from '@emotion-icons/fa-solid/Tags'
import { MapPin } from '@emotion-icons/fa-solid/MapPin'
import { MapMarkerAlt } from '@emotion-icons/fa-solid/MapMarkerAlt'
import { Book } from '@emotion-icons/fa-solid/Book'
import { BookOpen } from '@emotion-icons/fa-solid/BookOpen'
import { City } from '@emotion-icons/fa-solid/City'
import { GlobeAmericas } from '@emotion-icons/fa-solid/GlobeAmericas'
import { Bullseye } from '@emotion-icons/fa-solid/Bullseye'
import { FileInvoiceDollar } from '@emotion-icons/fa-solid/FileInvoiceDollar'
import { FireAlt } from '@emotion-icons/fa-solid/FireAlt'
import { Tree } from '@emotion-icons/fa-solid/Tree'
import { Rocket } from '@emotion-icons/fa-solid/Rocket'
import { Snowflake } from '@emotion-icons/fa-solid/Snowflake'
import { Seedling } from '@emotion-icons/fa-solid/Seedling'
import { Scroll } from '@emotion-icons/fa-solid/Scroll'
import { Leaf } from '@emotion-icons/fa-solid/Leaf'
import { INSIGHT_COLOR } from 'src/atoms/insight-builder'
import { InsightIconKey } from 'src/types/insight-builder'

export const INSIGHT_ICON_MAPPING = {
  // Nature Icons
  HandPeace,
  Leaf,
  Tree,
  Seedling,
  Snowflake,
  // Business Icons
  Briefcase,
  DollarSign,
  FileInvoiceDollar,
  Archive,
  Paperclip,
  // Building & Location Icons
  Building,
  City,
  GlobeAmericas,
  MapPin,
  MapMarkerAlt,
  Road,
  // Chart & Data Icons
  ChartArea,
  ChartBar,
  ChartLine,
  ChartPie,
  // Miscellaneous Icons
  PlusCircle,
  SmileWink,
  Star,
  Certificate,
  Bolt,
  DrawPolygon,
  Key,
  Tags,
  FileAlt,
  FolderOpen,
  Book,
  BookOpen,
  Bullseye,
  FireAlt,
  Rocket,
  Scroll,
} as const

export const getInsightIcon = (icon: InsightIconKey, size: number = 16, color: string = Colors.bgGreenDark) => {
  const Icon = INSIGHT_ICON_MAPPING[icon] || Star
  return <Icon size={size} color={INSIGHT_COLOR} />
}

export const InsightIcon = styled(TopToolbarIconBox)<{ dashed: 'true' | 'false' }>`
  margin: 0;
  border-style: ${(props) => (props.dashed === 'true' ? 'dashed' : 'solid')};
  * {
    pointer-events: none;
  }
  :hover {
    cursor: pointer;
    border-style: solid;
    box-shadow: ${Colors.BOX_SHADOW};
  }
`

const IconSelectorPopover = styled(Popover)`
  top: ${Shapes.spacing[2]}px;
  left: 0px;
  .MuiPaper-root {
    border: 1px solid ${Colors.borderInput};
    border-radius: ${Shapes.borderRadius}px;
    box-shadow: ${Colors.BOX_SHADOW};
    overflow: hidden;
  }
`
const IconSelectorPopoverContent = styled.div``
const IconSelectorPopoverHeader = styled.div`
  display: flex;
  padding: ${Shapes.spacing[4]}px;
  border-bottom: 1px solid ${Colors.border};
  background-color: ${Colors.bgWhite};
  height: ${Shapes.spacing[8]}px;
`
// 5 column grid
const IconSelectorList = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: ${Shapes.spacing[4]}px;
  padding: ${Shapes.spacing[4]}px;
  background-color: ${Colors.bgLight};
  max-height: 420px;
  overflow-y: auto;
  ${FANCY_SCROLLBAR}
`
type InsightIconSelectorProps = {
  icon: InsightIconKey
  onSelect: (icon: InsightIconKey) => void
}
export const InsightIconSelector = (props: InsightIconSelectorProps) => {
  const { icon, onSelect } = props

  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null)
  const open = Boolean(anchorEl)
  const id = open ? `popover-icon-selctor` : undefined

  const selectedIcon = getInsightIcon(icon, 20)

  const onOpen = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.target as HTMLDivElement)
  }
  const onClose = (_: any, reason: 'backdropClick' | 'escapeKeyDown') => {
    setAnchorEl(null)
  }

  const onSelectIcon = (icon: InsightIconKey) => () => {
    onSelect(icon)
    onClose(null, 'backdropClick')
  }
  return (
    <>
      <InsightIcon onClick={onOpen} dashed="true" color={INSIGHT_COLOR}>
        <TopToolbarIconBoxContent color={INSIGHT_COLOR}>
          {selectedIcon ? selectedIcon : <PlusCircle size={20} color={INSIGHT_COLOR} />}
        </TopToolbarIconBoxContent>
      </InsightIcon>
      <IconSelectorPopover
        style={{}}
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={onClose}
        anchorReference="anchorEl"
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}>
        <IconSelectorPopoverContent>
          <IconSelectorPopoverHeader>
            <Typography variant="textmd">Choose an Icon</Typography>
          </IconSelectorPopoverHeader>
          <IconSelectorList>
            {Object.keys(INSIGHT_ICON_MAPPING).map((iconKey) => {
              const Icon = INSIGHT_ICON_MAPPING[iconKey as InsightIconKey]
              return (
                <InsightIcon
                  key={iconKey}
                  onClick={onSelectIcon(iconKey as InsightIconKey)}
                  dashed="false"
                  color={INSIGHT_COLOR}>
                  <TopToolbarIconBoxContent color={INSIGHT_COLOR}>
                    <Icon size={20} color={INSIGHT_COLOR} />
                  </TopToolbarIconBoxContent>
                </InsightIcon>
              )
            })}
          </IconSelectorList>
        </IconSelectorPopoverContent>
      </IconSelectorPopover>
    </>
  )
}
